import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createChat } from '@n8n/chat';

type Agent = 'public' | 'private';

// Paths wrapped in <ProtectedRoute> in src/App.tsx. Any path starting with one
// of these prefixes gets the data-enabled "private" Guv'nor. Everything else
// (landing, login, register, /faq, /guide/*, /privacy-policy, /start-checkout,
// /start-checkout/betfair, /betfair, /resume-signup, etc.) gets the sales-focused "public" Guv'nor.
const PRIVATE_PREFIXES = [
  '/dashboard',
  '/today-races',
  '/today-bets',
  '/open-bets',
  '/results',
  '/performance',
  '/settings',
  '/subscribe',
];

const isPrivateRoute = (pathname: string): boolean =>
  PRIVATE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );

const AGENT_CONFIG: Record<
  Agent,
  { webhookUrl: string; initialMessage: string }
> = {
  private: {
    webhookUrl:
      'https://n8n.alba-nova.com/webhook/2f519e36-3f6c-4158-9740-0e059cdd01ae/chat',
    initialMessage: "Alright, mate. The Guv'nor's in - what d'ya need?",
  },
  public: {
    webhookUrl:
      'https://n8n.alba-nova.com/webhook/bc4a884a-95ba-46c9-a444-f3567e61c009/chat',
    initialMessage:
      "Oi! The Guv'nor 'ere. Thinkin' about gettin' on board? Ask me anythin'.",
  },
};

const I18N = {
  en: {
    title: "🐴 The Guv'Nor",
    subtitle: "What d'ya need?",
    footer: '',
    getStarted: 'New Conversation',
    inputPlaceholder: 'Type your question..',
    closeButtonTooltip: 'Close chat',
  },
};

export default function ChatWidget() {
  const { pathname } = useLocation();
  const appRef = useRef<ReturnType<typeof createChat> | null>(null);
  const currentAgentRef = useRef<Agent | null>(null);

  useEffect(() => {
    const desired: Agent = isPrivateRoute(pathname) ? 'private' : 'public';
    if (currentAgentRef.current === desired) return;

    if (appRef.current) {
      try {
        appRef.current.unmount();
      } catch {
        // Ignore unmount errors (e.g. during HMR or edge cases)
      }
      appRef.current = null;
    }

    const host = document.getElementById('n8n-chat');
    if (host) host.innerHTML = '';

    const cfg = AGENT_CONFIG[desired];
    appRef.current = createChat({
      webhookUrl: cfg.webhookUrl,
      target: '#n8n-chat',
      mode: 'window',
      showWelcomeScreen: false,
      loadPreviousSession: true,
      initialMessages: [cfg.initialMessage],
      i18n: I18N,
    });
    currentAgentRef.current = desired;
  }, [pathname]);

  useEffect(() => {
    return () => {
      if (appRef.current) {
        try {
          appRef.current.unmount();
        } catch {
          // Ignore
        }
        appRef.current = null;
      }
      currentAgentRef.current = null;
    };
  }, []);

  return null;
}
